import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { MdClose, MdLocationOn } from "react-icons/md";
import WarningGlyph from "components/WarningGlyph";
import { readToken, readUser } from "utils/session";
import { deriveAccess } from "utils/access";
import { PUBLIC_ASSET_ICON } from "components/AccountImage";
import { libraries } from "./FleetMap";
import { pointPosition } from "components/RoutePlot";
import { reportIsStale } from "utils/reportAge";

function shown(value) {
  if (value == null || value === "") return "-";
  return String(value).replace(/ GMT$/, "");
}

function placeName(row, kind) {
  if (!row) return "";
  if (kind === "zone") {
    return row.branch?.zone?.name || row.branch?.zoneName || row.zoneName || "";
  }
  return row.branchName || row.branch?.name || "";
}

function AssetLocationMap({ lat, lng, icon }) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
    version: "3.64",
  });
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);
  const hasPoint = Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 0 && longitude === 0);

  if (!hasPoint) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-sm text-gray-500">
        Valid location is not available.
      </div>
    );
  }
  if (!isLoaded) {
    return <div className="flex h-full items-center justify-center text-sm text-gray-500">Loading map...</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={{ height: "100%", width: "100%" }}
      center={{ lat: latitude, lng: longitude }}
      zoom={16}
      options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy" }}
    >
      <Marker
        position={{ lat: latitude, lng: longitude }}
        icon={
          window.google
            ? {
                url: icon || PUBLIC_ASSET_ICON,
                scaledSize: new window.google.maps.Size(30, 50),
                anchor: new window.google.maps.Point(15, 25),
              }
            : undefined
        }
      />
    </GoogleMap>
  );
}

const AssetDetailsPopup = ({ isOpen, onClose, deviceId, fleetRow }) => {
  const navigate = useNavigate();
  const access = deriveAccess(readUser());
  const canLiveTrack = !access || access.canLiveTrack;
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [address, setAddress] = useState("");
  const [isAddressLoading, setIsAddressLoading] = useState(false);

  const USERKEY = readToken();

  useEffect(() => {
    if (!isOpen || !deviceId) return;

    const fetchDetails = async () => {
      setIsLoading(true);
      setError("");
      setAddress("");
      setIsAddressLoading(false);
      setData(null);

      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/getassetdetail`;
        const payload = { repDid: [deviceId] };
        const response = await axios.get(url, {
          params: { reportData: JSON.stringify(payload) },
          headers: { Authorization: `Bearer ${USERKEY}` },
        });
        setData(response.data);
      } catch (err) {
        setError("Failed to load asset details.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [isOpen, deviceId, USERKEY]);

  useEffect(() => {
    const plotted = pointPosition(data?.assetData?.[0]) || pointPosition(fleetRow);

    if (plotted) {
      setIsAddressLoading(true);
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: plotted }, (results, status) => {
          if (status === "OK" && results[0]) setAddress(results[0].formatted_address);
          else setAddress(`Location not found (${status})`);
          setIsAddressLoading(false);
        });
      } else {
        setAddress("Maps API not loaded.");
        setIsAddressLoading(false);
      }
    } else if (data) {
      setAddress("Valid location is not available.");
    }
  }, [data, fleetRow]);

  if (!isOpen) return null;

  const packet = data?.assetData?.[0] || {};
  const plotted = pointPosition(packet) || pointPosition(fleetRow);
  const table = data?.assetDetails?.deviceDetailsTable || [];
  const assetId = table.find((item) => item.name === "Asset Id")?.value || fleetRow?.astid || "Unknown Asset";
  const zoneName = placeName(fleetRow, "zone") || "—";
  const branchName = placeName(fleetRow, "branch") || "—";
  const known = new Set(table.map((item) => String(item.name || "").toLowerCase()));
  const extras = [
    { name: "Latitude", value: plotted?.lat ?? packet.lat ?? fleetRow?.lat, aliases: ["latitude", "lat"] },
    { name: "Longitude", value: plotted?.lng ?? packet.lng ?? fleetRow?.lng, aliases: ["longitude", "lng", "long"] },
    { name: "Speed", value: packet.speed ?? fleetRow?.speed, aliases: ["speed"] },
    { name: "Direction", value: packet.dirn ?? fleetRow?.dirn, aliases: ["direction"] },
    { name: "Warning", value: packet.warn ?? fleetRow?.warn, aliases: ["warning"] },
    { name: "Motion", value: packet.motstat ?? fleetRow?.motstat, aliases: ["motion"] },
  ].filter((item) => item.aliases.every((alias) => !known.has(alias)));
  const details = [...table, ...extras];
  const icon = data?.assetDetails?.AssetImg || fleetRow?.AssetMapImg || PUBLIC_ASSET_ICON;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end bg-black/60" onClick={onClose}>
      <aside
        className="asset-slide-in flex h-full w-full max-w-6xl flex-col bg-white shadow-2xl dark:bg-navy-800"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-6 py-4 dark:border-white/10">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            {data?.assetDetails?.displayName || "Asset Details"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-navy-700"
          >
            <MdClose className="h-5 w-5 text-gray-600 dark:text-white" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 bg-lightPrimary px-6 py-4 dark:bg-navy-900">
          <img
            src={icon}
            alt="Asset"
            className="h-16 w-16 object-contain"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = PUBLIC_ASSET_ICON;
            }}
          />
          <div>
            <p className="text-lg font-bold text-navy-700 dark:text-white">{assetId}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Zone {zoneName} · Branch {branchName}
            </p>
          </div>
          {canLiveTrack && (
            <button
              type="button"
              disabled={!deviceId}
              onClick={() => {
                navigate("/admin/Live-Tracking", {
                  state: {
                    liveDid: deviceId,
                    liveAid: fleetRow?.aid || fleetRow?.astid,
                  },
                });
              }}
              className="ml-auto rounded-md bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-600 disabled:opacity-50"
            >
              Live track
            </button>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex h-40 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"></div>
              </div>
            ) : error ? (
              <div className="flex h-40 items-center justify-center font-medium text-red-500">{error}</div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {details.map((item) => {
                  const warning = item.name === "Warning" && String(item.value ?? "").trim().toLowerCase() === "yes";
                  const value =
                    item.name === "Direction" && item.value != null && item.value !== ""
                      ? `${item.value}°`
                      : shown(item.value);
                  return (
                    <div
                      key={item.name}
                      className="flex flex-col rounded-xl border border-gray-100 bg-white p-3 shadow-sm dark:border-navy-700 dark:bg-navy-800"
                    >
                      <span className="text-xs text-gray-500 dark:text-gray-400">{item.name}</span>
                      <span className="flex items-center gap-1 truncate text-sm font-bold text-navy-700 dark:text-white" title={value}>
                        {warning && (
                          <WarningGlyph
                            motion={packet.motstat ?? fleetRow?.motstat}
                            stale={reportIsStale(packet.lastupdt || fleetRow?.lastupdt)}
                            className="h-4 w-4 shrink-0"
                          />
                        )}
                        {value}
                      </span>
                    </div>
                  );
                })}
                <div className="col-span-2 flex items-start gap-3 rounded-xl border border-brand-100 bg-brand-50 p-4 dark:border-navy-700 dark:bg-navy-800">
                  <div className="mt-0.5 rounded-full bg-brand-100 p-2 dark:bg-navy-700">
                    <MdLocationOn className="h-5 w-5 text-brand-500 dark:text-white" />
                  </div>
                  <div className="flex w-full flex-col">
                    <span className="mb-1 text-xs text-gray-500 dark:text-gray-400">Live Location</span>
                    {isAddressLoading ? (
                      <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-navy-600"></div>
                    ) : (
                      <span className="text-sm font-bold text-navy-700 dark:text-white">{address || "—"}</span>
                    )}
                    {plotted && (
                      <span className="mt-1 text-xs text-gray-400">
                        Lat: {plotted.lat}, Lng: {plotted.lng}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="h-72 w-full shrink-0 border-t border-gray-200 dark:border-white/10 md:h-auto md:w-[46%] md:border-l md:border-t-0">
            <AssetLocationMap lat={plotted?.lat} lng={plotted?.lng} icon={icon} />
          </div>
        </div>
      </aside>
    </div>
  );
};

export default AssetDetailsPopup;
